import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  addDoc,
  collection,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { encryptPatientPayload } from "../utils/encryption";
import doctorImage from "../images/WhatsApp_Image_2026-01-06_at_2.57.47_PM-removebg-preview (1).png";

const animationStyles = `
  * {
    box-sizing: border-box;
  }

  @keyframes fadeInBounce {
    0% {
      opacity: 0;
      transform: scale(0.8) translateY(20px);
    }
    60% {
      opacity: 1;
      transform: scale(1.05) translateY(-5px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @keyframes typing {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }

  @keyframes typingCursor {
    0%, 49% {
      border-right: 3px solid rgba(255, 255, 255, 0.8);
    }
    50%, 100% {
      border-right: 3px solid transparent;
    }
  }

  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  /* Responsive Styles */
  @media (max-width: 768px) {
    .greeting-container,
    .questions-container,
    .success-container {
      flex-direction: row !important;
      gap: 4px !important;
      padding: 6px !important;
      position: relative !important;
      width: 100% !important;
      max-width: 100% !important;
      align-items: flex-start !important;
    }

    .doctor-character {
      height: 160px !important;
      width: 120px !important;
      margin-right: 4px !important;
      margin-bottom: 0 !important;
      flex-shrink: 0 !important;
    }

    .doctor-character img {
      max-height: 160px !important;
    }

    .content-box {
      padding: 14px !important;
      min-height: auto !important;
      width: auto !important;
      flex: 1 !important;
      overflow: visible !important;
      margin-top: 0 !important;
      font-size: 13px !important;
      border-radius: 12px !important;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1) !important;
    }

    .content-box h2,
    .content-box h3 {
      font-size: 15px !important;
      margin: 0 0 10px 0 !important;
    }

    .content-box p {
      font-size: 12px !important;
      margin: 0 0 10px 0 !important;
      line-height: 1.4 !important;
    }

    .doctor-message h2 {
      font-size: 14px !important;
      margin: 0 !important;
    }

    .speech-pointer {
      display: none !important;
    }

    .speech-pointer-mobile {
      display: block !important;
      position: absolute !important;
      top: -12px !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
      width: 0 !important;
      height: 0 !important;
      border-left: 10px solid transparent !important;
      border-right: 10px solid transparent !important;
      border-bottom: 12px solid white !important;
      filter: drop-shadow(0px -1px 1px rgba(0, 0, 0, 0.06)) !important;
    }

    input,
    select {
      font-size: 14px !important;
      padding: 8px 10px !important;
      border-radius: 6px !important;
    }

    button {
      font-size: 13px !important;
      padding: 8px 14px !important;
      border-radius: 6px !important;
    }

    .user-message {
      padding: 8px !important;
      font-size: 12px !important;
      margin-top: 8px !important;
      border-radius: 6px !important;
    }
  }

  @media (max-width: 480px) {
    .greeting-container,
    .questions-container,
    .success-container {
      flex-direction: row !important;
      gap: 2px !important;
      padding: 4px !important;
      align-items: flex-start !important;
    }

    .doctor-character {
      height: 140px !important;
      width: 100px !important;
      margin-right: 3px !important;
      margin-bottom: 0 !important;
      flex-shrink: 0 !important;
    }

    .doctor-character img {
      max-height: 140px !important;
    }

    .content-box {
      padding: 12px !important;
      font-size: 12px !important;
      border-radius: 10px !important;
      flex: 1 !important;
    }

    .content-box h2,
    .content-box h3 {
      font-size: 13px !important;
      margin: 0 0 8px 0 !important;
    }

    .content-box p {
      font-size: 11px !important;
      margin: 0 0 8px 0 !important;
      line-height: 1.3 !important;
    }

    .doctor-message h2 {
      font-size: 13px !important;
    }

    input,
    select {
      font-size: 13px !important;
      padding: 7px 8px !important;
    }

    button {
      font-size: 12px !important;
      padding: 7px 12px !important;
    }

    .speech-pointer-mobile {
      top: -10px !important;
      border-left: 8px solid transparent !important;
      border-right: 8px solid transparent !important;
      border-bottom: 10px solid white !important;
    }
  }

  @media (min-width: 769px) {
    .speech-pointer-mobile {
      display: none !important;
    }
  }

  .doctor-character {
    animation: fadeInBounce 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .typing-text {
    overflow: hidden;
    display: inline-block;
    border-right: 3px solid rgba(255, 255, 255, 0.8);
    white-space: normal;
    animation: typingCursor 0.7s infinite;
    padding-right: 2px;
  }

  .doctor-message {
    animation: slideInFromRight 0.4s ease-out;
  }

  .user-message {
    animation: slideInFromRight 0.4s ease-out;
  }

  .game-button {
    position: relative;
    overflow: hidden;
  }

  .game-button::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
  }

  .game-button:hover::before {
    left: 100%;
  }

  /* Header responsive */
  @media (max-width: 480px) {
    h2 {
      font-size: 18px !important;
    }
  }
`;

// Inject styles
if (
  typeof document !== "undefined" &&
  !document.getElementById("addPatientStyles")
) {
  const styleSheet = document.createElement("style");
  styleSheet.id = "addPatientStyles";
  styleSheet.textContent = animationStyles;
  document.head.appendChild(styleSheet);
}

const QUESTIONS = [
  {
    field: "name",
    question: "What is the patient's full name?",
    type: "text",
    placeholder: "e.g., Marcus Thompson",
  },
  {
    field: "age",
    question: "How old is the patient?",
    type: "number",
    placeholder: "e.g., 35",
  },
  {
    field: "gender",
    question: "What is the patient's gender?",
    type: "select",
    options: ["Male", "Female", "Other"],
  },
  {
    field: "phone",
    question: "What is the patient's phone number?",
    type: "tel",
    placeholder: "(555) 123-4567",
  },
  {
    field: "email",
    question: "What is the patient's email address? (optional)",
    type: "email",
    placeholder: "patient@example.com",
    optional: true,
  },
  {
    field: "address",
    question: "What is the patient's address? (optional)",
    type: "text",
    placeholder: "Street address, City, State, ZIP code",
    optional: true,
  },
];

const BILLING_QUESTIONS = [
  {
    field: "billingType",
    question: "Which billing type do you prefer?",
    type: "select",
    options: ["Package", "Per Session Fee"],
  },
];

function AddPatientPage() {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [displayedText, setDisplayedText] = useState("");
  const [showButton, setShowButton] = useState(false);

  const [showGuide, setShowGuide] = useState(false); // Toggle between form and guided mode
  const [stage, setStage] = useState("greeting"); // greeting, questions, billing, success
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [questionsAsked, setQuestionsAsked] = useState(new Set()); // Track which questions have been asked
  const [displayedQuestion, setDisplayedQuestion] = useState(""); // For typing animation of questions
  const [showQuestionInput, setShowQuestionInput] = useState(false); // Show input after question is fully typed
  const [form, setForm] = useState({
    name: "",
    age: "",
    gender: "Male",
    phone: "",
    email: "",
    address: "",
  });
  const [billingForm, setBillingForm] = useState({
    billingType: "package",
    selectedPackage: "",
    perSessionFee: "",
    paymentType: "prepaid",
    appointmentCount: "",
  });
  const [packages, setPackages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newPatientId, setNewPatientId] = useState(null);

  const greetingText =
    "I'm here to help you add a new patient. Let's get started by answering a few quick questions!";

  useEffect(() => {
    const loadPackages = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          setPackages(userData.packages || []);
        }
      } catch (e) {
        console.error("Failed to load packages", e);
      }
    };
    loadPackages();

    // Typing animation for greeting text (only when guide is active)
    if (showGuide && stage === "greeting") {
      let index = 0;
      setDisplayedText("");
      setShowButton(false);

      const typingInterval = setInterval(() => {
        if (index < greetingText.length) {
          setDisplayedText(greetingText.slice(0, index + 1));
          index++;
        } else {
          clearInterval(typingInterval);
          setShowButton(true);
        }
      }, 30);

      return () => clearInterval(typingInterval);
    }
  }, [stage, showGuide]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addDoctorMessage = (text) => {
    setMessages((prev) => [
      ...prev,
      { type: "doctor", text, timestamp: Date.now() },
    ]);
  };

  const addUserMessage = (text) => {
    setMessages((prev) => [
      ...prev,
      { type: "user", text, timestamp: Date.now() },
    ]);
  };

  const handleGreetingStart = () => {
    addUserMessage("Yes, let's add a patient!");
    setTimeout(() => {
      addDoctorMessage(
        `Great! I'll ask you some questions to add the patient. Let me start...`
      );
      setTimeout(() => {
        setStage("questions");
        setCurrentQuestionIndex(0);
        setQuestionsAsked(new Set());
        setTimeout(() => {
          askNextQuestion();
        }, 300);
      }, 500);
    }, 300);
  };

  const askNextQuestion = () => {
    if (currentQuestionIndex < QUESTIONS.length) {
      const question = QUESTIONS[currentQuestionIndex];
      const key = `q${currentQuestionIndex}`;

      // Only ask if we haven't asked this question before
      if (!questionsAsked.has(key)) {
        setQuestionsAsked((prev) => new Set([...prev, key]));
        setDisplayedQuestion("");
        setShowQuestionInput(false);

        // Typing animation for question
        let index = 0;
        const typingInterval = setInterval(() => {
          if (index < question.question.length) {
            setDisplayedQuestion(question.question.slice(0, index + 1));
            index++;
          } else {
            clearInterval(typingInterval);
            setShowQuestionInput(true);
          }
        }, 30);
      }
    } else {
      // Move to billing stage
      setStage("billing");
      setCurrentQuestionIndex(0);
      setQuestionsAsked(new Set());
      setDisplayedQuestion("");
      setShowQuestionInput(false);
      addDoctorMessage(
        "Perfect! Now let's set up the billing information for this patient."
      );
      setTimeout(() => {
        askBillingQuestion();
      }, 500);
    }
  };

  const askBillingQuestion = () => {
    if (currentQuestionIndex < BILLING_QUESTIONS.length) {
      const question = BILLING_QUESTIONS[currentQuestionIndex];
      const key = `bill${currentQuestionIndex}`;

      if (!questionsAsked.has(key)) {
        setQuestionsAsked((prev) => new Set([...prev, key]));
        setDisplayedQuestion("");
        setShowQuestionInput(false);

        // Typing animation for billing question
        let index = 0;
        const typingInterval = setInterval(() => {
          if (index < question.question.length) {
            setDisplayedQuestion(question.question.slice(0, index + 1));
            index++;
          } else {
            clearInterval(typingInterval);
            setShowQuestionInput(true);
          }
        }, 30);
      }
    } else {
      // Complete the patient addition
      completePatientAddition();
    }
  };

  const handleUserAnswer = (answer) => {
    if (stage === "questions") {
      const question = QUESTIONS[currentQuestionIndex];

      // Validate answer
      if (!answer.trim() && !question.optional) {
        addDoctorMessage(`Please provide the ${question.field}. 😊`);
        return;
      }

      addUserMessage(answer);

      // Update form
      setForm((prev) => ({
        ...prev,
        [question.field]: answer || "",
      }));

      setUserInput("");

      // Move to next question
      setTimeout(() => {
        const nextIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIndex);
        setDisplayedQuestion("");
        setShowQuestionInput(false);

        if (nextIndex < QUESTIONS.length) {
          const nextQuestion = QUESTIONS[nextIndex];
          // Typing animation for next question
          let index = 0;
          const typingInterval = setInterval(() => {
            if (index < nextQuestion.question.length) {
              setDisplayedQuestion(nextQuestion.question.slice(0, index + 1));
              index++;
            } else {
              clearInterval(typingInterval);
              setShowQuestionInput(true);
            }
          }, 30);
        } else {
          addDoctorMessage(
            "Great! Now let's set up the billing information..."
          );
          setStage("billing");
          setCurrentQuestionIndex(0);
          setQuestionsAsked(new Set());
          setTimeout(() => {
            askBillingQuestion();
          }, 300);
        }
      }, 300);
    } else if (stage === "billing") {
      addUserMessage(answer);

      // Update billing form
      const selectedBilling =
        answer.toLowerCase() === "package" ? "package" : "perSession";
      setBillingForm((prev) => ({ ...prev, billingType: selectedBilling }));

      setUserInput("");

      // Move to next billing question or complete
      setTimeout(() => {
        const nextIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIndex);

        if (selectedBilling === "package") {
          askPackageDetails();
        } else {
          askPerSessionFee();
        }
      }, 300);
    }
  };

  const askPackageDetails = () => {
    addDoctorMessage("Which package would you like to select?");
  };

  const askPerSessionFee = () => {
    addDoctorMessage("What is the per session fee in INR?");
  };

  const completePatientAddition = async () => {
    setSaving(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError("You must be logged in to add patients");
        setSaving(false);
        return;
      }

      // Add patient
      const docRef = await addDoc(
        collection(db, "patients"),
        encryptPatientPayload({
          name: form.name,
          age: form.age,
          gender: form.gender,
          phone: form.phone,
          email: form.email || "",
          address: form.address || "",
          complaint: "",
          history: "",
          status: "New",
          userId: currentUser.uid,
          createdAt: serverTimestamp(),
        }),
      );

      // Add billing info
      const updateData = {
        paymentType: billingForm.paymentType,
        paymentFrequency: "per_session",
      };

      if (
        billingForm.billingType === "package" &&
        billingForm.selectedPackage
      ) {
        updateData.selectedPackage = billingForm.selectedPackage;
        updateData.appointmentCount =
          parseInt(billingForm.appointmentCount) || 0;
        const selectedPkg = packages.find(
          (pkg) => pkg.name === billingForm.selectedPackage
        );
        if (selectedPkg && selectedPkg.lumpSumValue) {
          const appointmentCount =
            parseInt(billingForm.appointmentCount) ||
            selectedPkg.appointmentCount;
          updateData.sessionFee =
            parseFloat(selectedPkg.lumpSumValue) / appointmentCount;
        }
      } else if (billingForm.perSessionFee) {
        updateData.sessionFee = parseFloat(billingForm.perSessionFee) || 0;
        updateData.selectedPackage = null;
        updateData.appointmentCount = null;
      }

      await updateDoc(doc(db, "patients", docRef.id), updateData);

      setNewPatientId(docRef.id);

      if (showGuide) {
        setStage("success");
        addDoctorMessage(
          `🎉 Excellent! Patient ${form.name} has been successfully added! You can now navigate to the patients list.`
        );
      } else {
        // Navigate directly to patients list
        navigate("/patients", { replace: true });
      }
    } catch (err) {
      setError(err.message || "Failed to save patient");
      if (showGuide) {
        addDoctorMessage(
          `Sorry, there was an error: ${err.message}. Please try again.`
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!form.name || !form.age || !form.phone) {
      setError("Please fill in all required fields");
      return;
    }

    setError("");
    await completePatientAddition();
  };

  const getCurrentQuestion = () => {
    if (stage === "questions" && currentQuestionIndex < QUESTIONS.length) {
      return QUESTIONS[currentQuestionIndex];
    }
    if (
      stage === "billing" &&
      currentQuestionIndex < BILLING_QUESTIONS.length
    ) {
      return BILLING_QUESTIONS[currentQuestionIndex];
    }
    return null;
  };

  const question = getCurrentQuestion();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "#f5f5f5",
      }}
    >
      {/* Header */}
      <header
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "white",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "white",
            border: "1px solid #e0e0e0",
            fontSize: "20px",
            cursor: "pointer",
            color: "#333",
            width: "36px",
            height: "36px",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s ease",
            padding: "0",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#f5f5f5";
            e.target.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "white";
            e.target.style.transform = "scale(1)";
          }}
        >
          ←
        </button>
        <h2
          style={{
            margin: 0,
            fontSize: "20px",
            fontWeight: "700",
            color: "#333",
          }}
        >
          Add Patient
        </h2>
        <button
          onClick={() => {
            setShowGuide(!showGuide);
            if (!showGuide) {
              setStage("greeting");
              setCurrentQuestionIndex(0);
              setMessages([]);
              setQuestionsAsked(new Set());
            }
          }}
          style={{
            background: showGuide ? "#f5f5f5" : "var(--primary)",
            border: showGuide ? "1px solid #e0e0e0" : "none",
            fontSize: "14px",
            cursor: "pointer",
            color: showGuide ? "#333" : "white",
            padding: "8px 16px",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            transition: "all 0.3s ease",
            fontWeight: "600",
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = "scale(1)";
          }}
        >
          {showGuide ? "✕ Close Guide" : "❓ Help"}
        </button>
      </header>

      {/* Main Content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8px",
          overflow: "auto",
          position: "relative",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Show Form Mode */}
        {!showGuide && (
          <div
            style={{
              maxWidth: "800px",
              width: "100%",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              padding: "32px",
              boxSizing: "border-box",
            }}
          >
            <h3
              style={{
                fontSize: "24px",
                marginBottom: "24px",
                color: "#333",
                fontWeight: "700",
              }}
            >
              Patient Information
            </h3>

            <form onSubmit={handleFormSubmit}>
              {/* Patient Details Section */}
              <div style={{ marginBottom: "32px" }}>
                <h4
                  style={{
                    fontSize: "16px",
                    marginBottom: "16px",
                    color: "#666",
                    fontWeight: "600",
                  }}
                >
                  Personal Details
                </h4>

                <div style={{ display: "grid", gap: "16px" }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      Full Name <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                      placeholder="e.g., Marcus Thompson"
                      required
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                        fontSize: "14px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#333",
                        }}
                      >
                        Age <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="number"
                        value={form.age}
                        onChange={(e) =>
                          setForm({ ...form, age: e.target.value })
                        }
                        placeholder="e.g., 35"
                        required
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "1px solid #e0e0e0",
                          fontSize: "14px",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#333",
                        }}
                      >
                        Gender <span style={{ color: "red" }}>*</span>
                      </label>
                      <select
                        value={form.gender}
                        onChange={(e) =>
                          setForm({ ...form, gender: e.target.value })
                        }
                        required
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "1px solid #e0e0e0",
                          fontSize: "14px",
                          boxSizing: "border-box",
                        }}
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      Phone Number <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) =>
                        setForm({ ...form, phone: e.target.value })
                      }
                      placeholder="(555) 123-4567"
                      required
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                        fontSize: "14px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      Email Address (Optional)
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) =>
                        setForm({ ...form, email: e.target.value })
                      }
                      placeholder="patient@example.com"
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                        fontSize: "14px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      Address (Optional)
                    </label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(e) =>
                        setForm({ ...form, address: e.target.value })
                      }
                      placeholder="Street address, City, State, ZIP code"
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                        fontSize: "14px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div
                  style={{
                    padding: "12px",
                    background: "#fee",
                    color: "#c33",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    border: "1px solid #fcc",
                    fontSize: "14px",
                  }}
                >
                  {error}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  style={{
                    padding: "12px 24px",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                    background: "white",
                    color: "#333",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.3s ease",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: "12px 24px",
                    borderRadius: "8px",
                    border: "none",
                    background: saving ? "#ccc" : "var(--primary)",
                    color: "white",
                    fontWeight: "600",
                    cursor: saving ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    transition: "all 0.3s ease",
                  }}
                >
                  {saving ? "Saving..." : "Add Patient"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Show Guide Mode */}
        {showGuide && (
          <>
            {stage === "greeting" && (
              <div
                className="greeting-container"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "32px",
                  alignItems: "center",
                  justifyContent: "center",
                  maxWidth: "1000px",
                  width: "100%",
                  animation: "slideInFromRight 0.6s ease-out",
                  boxSizing: "border-box",
                }}
              >
                {/* Doctor Character */}
                <div
                  className="doctor-character"
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    height: "340px",
                    width: "300px",
                  }}
                >
                  <img
                    src={doctorImage}
                    alt="Doctor"
                    style={{
                      height: "100%",
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                </div>

                {/* Greeting Content */}
                <div
                  className="content-box"
                  style={{
                    position: "relative",
                    flex: 1,
                    padding: "40px",
                    background: "white",
                    borderRadius: "20px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
                    border: "1px solid #e8e8e8",
                    minHeight: "280px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minWidth: "300px",
                  }}
                >
                  {/* Speech Bubble Pointer */}
                  <div
                    className="speech-pointer"
                    style={{
                      position: "absolute",
                      left: "-20px",
                      top: "60px",
                      width: "0",
                      height: "0",
                      borderTop: "15px solid transparent",
                      borderBottom: "15px solid transparent",
                      borderRight: "20px solid white",
                      filter: "drop-shadow(-2px 0px 2px rgba(0, 0, 0, 0.08))",
                    }}
                  />
                  {/* Mobile Speech Bubble Pointer */}
                  <div className="speech-pointer-mobile" />
                  <div>
                    <h3
                      style={{
                        fontSize: "32px",
                        marginBottom: "16px",
                        color: "#333",
                        fontWeight: "800",
                      }}
                    >
                      Hello! 👋
                    </h3>
                    <p
                      style={{
                        fontSize: "16px",
                        color: "#666",
                        lineHeight: "1.8",
                        minHeight: "90px",
                        fontWeight: "500",
                      }}
                    >
                      <span className="typing-text">{displayedText}</span>
                    </p>
                  </div>

                  {showButton && (
                    <button
                      onClick={handleGreetingStart}
                      className="game-button"
                      style={{
                        padding: "14px 28px",
                        borderRadius: "8px",
                        border: "none",
                        background: "var(--primary)",
                        color: "white",
                        fontWeight: "700",
                        cursor: "pointer",
                        fontSize: "16px",
                        transition: "all 0.3s ease",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                        transform: "translateY(0)",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "translateY(-3px)";
                        e.target.style.boxShadow =
                          "0 4px 16px rgba(0, 0, 0, 0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow =
                          "0 2px 8px rgba(0, 0, 0, 0.1)";
                      }}
                    >
                      Let's Start! 🚀
                    </button>
                  )}
                </div>
              </div>
            )}

            {(stage === "questions" || stage === "billing") && (
              <div
                className="questions-container"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "32px",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  maxWidth: "1000px",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                {/* Doctor Character on Left */}
                <div
                  className="doctor-character"
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    height: "300px",
                    width: "280px",
                  }}
                >
                  <img
                    src={doctorImage}
                    alt="Doctor"
                    style={{
                      height: "100%",
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                </div>

                {/* Questions Content on Right */}
                <div
                  className="content-box"
                  style={{
                    position: "relative",
                    flex: 1,
                    padding: "40px",
                    background: "white",
                    borderRadius: "20px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
                    border: "1px solid #e8e8e8",
                    minHeight: "260px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minWidth: "300px",
                  }}
                >
                  {/* Speech Bubble Pointer */}
                  <div
                    className="speech-pointer"
                    style={{
                      position: "absolute",
                      left: "-20px",
                      top: "60px",
                      width: "0",
                      height: "0",
                      borderTop: "15px solid transparent",
                      borderBottom: "15px solid transparent",
                      borderRight: "20px solid white",
                      filter: "drop-shadow(-2px 0px 2px rgba(0, 0, 0, 0.08))",
                    }}
                  />
                  {/* Mobile Speech Bubble Pointer */}
                  <div className="speech-pointer-mobile" />
                  <div className="doctor-message">
                    <h2
                      style={{
                        fontSize: "20px",
                        color: "#333",
                        marginBottom: "20px",
                        fontWeight: "700",
                        lineHeight: "1.4",
                      }}
                    >
                      <span className="typing-text">{displayedQuestion}</span>
                    </h2>
                  </div>

                  {/* Input Area */}
                  {showQuestionInput && (
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      {getCurrentQuestion()?.type === "select" ? (
                        <>
                          <select
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter" && userInput.trim()) {
                                handleUserAnswer(userInput);
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: "12px 14px",
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                              background: "#f9f9f9",
                              color: "#333",
                              fontSize: "16px",
                              fontWeight: "500",
                              transition: "all 0.3s ease",
                            }}
                            onFocus={(e) => {
                              e.target.style.background = "white";
                              e.target.style.borderColor = "var(--primary)";
                              e.target.style.boxShadow =
                                "0 0 0 3px rgba(0, 0, 0, 0.05)";
                            }}
                            onBlur={(e) => {
                              e.target.style.background = "#f9f9f9";
                              e.target.style.borderColor = "#e0e0e0";
                              e.target.style.boxShadow = "none";
                            }}
                            autoFocus
                          >
                            <option value="">Select an option...</option>
                            {getCurrentQuestion()?.options?.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleUserAnswer(userInput)}
                            disabled={!userInput.trim()}
                            className="game-button"
                            style={{
                              padding: "12px 24px",
                              borderRadius: "8px",
                              border: "none",
                              background: userInput.trim()
                                ? "var(--primary)"
                                : "#ccc",
                              color: "white",
                              fontWeight: "600",
                              cursor: userInput.trim()
                                ? "pointer"
                                : "not-allowed",
                              fontSize: "16px",
                              transition: "all 0.3s ease",
                              boxShadow: userInput.trim()
                                ? "0 2px 8px rgba(0, 0, 0, 0.1)"
                                : "none",
                            }}
                            onMouseEnter={(e) => {
                              if (userInput.trim()) {
                                e.target.style.transform = "translateY(-3px)";
                                e.target.style.boxShadow =
                                  "0 4px 16px rgba(0, 0, 0, 0.15)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow =
                                "0 2px 8px rgba(0, 0, 0, 0.1)";
                            }}
                          >
                            Send
                          </button>
                        </>
                      ) : (
                        <>
                          <input
                            type={getCurrentQuestion()?.type}
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                handleUserAnswer(userInput);
                              }
                            }}
                            placeholder={getCurrentQuestion()?.placeholder}
                            style={{
                              flex: 1,
                              padding: "12px 14px",
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                              background: "#f9f9f9",
                              color: "#333",
                              fontSize: "16px",
                              fontWeight: "500",
                              transition: "all 0.3s ease",
                            }}
                            onFocus={(e) => {
                              e.target.style.background = "white";
                              e.target.style.borderColor = "var(--primary)";
                              e.target.style.boxShadow =
                                "0 0 0 3px rgba(0, 0, 0, 0.05)";
                            }}
                            onBlur={(e) => {
                              e.target.style.background = "#f9f9f9";
                              e.target.style.borderColor = "#e0e0e0";
                              e.target.style.boxShadow = "none";
                            }}
                            autoFocus
                          />
                          <button
                            onClick={() => handleUserAnswer(userInput)}
                            disabled={
                              !userInput.trim() &&
                              !getCurrentQuestion()?.optional
                            }
                            className="game-button"
                            style={{
                              padding: "12px 24px",
                              borderRadius: "8px",
                              border: "none",
                              background:
                                userInput.trim() ||
                                getCurrentQuestion()?.optional
                                  ? "var(--primary)"
                                  : "#ccc",
                              color: "white",
                              fontWeight: "600",
                              cursor:
                                userInput.trim() ||
                                getCurrentQuestion()?.optional
                                  ? "pointer"
                                  : "not-allowed",
                              fontSize: "16px",
                              transition: "all 0.3s ease",
                              boxShadow:
                                userInput.trim() ||
                                getCurrentQuestion()?.optional
                                  ? "0 2px 8px rgba(0, 0, 0, 0.1)"
                                  : "none",
                            }}
                            onMouseEnter={(e) => {
                              if (
                                userInput.trim() ||
                                getCurrentQuestion()?.optional
                              ) {
                                e.target.style.transform = "translateY(-3px)";
                                e.target.style.boxShadow =
                                  "0 4px 16px rgba(0, 0, 0, 0.15)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow =
                                "0 2px 8px rgba(0, 0, 0, 0.1)";
                            }}
                          >
                            Send
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {error && (
                    <div
                      className="user-message"
                      style={{
                        padding: "12px",
                        background: "#fee",
                        color: "#c33",
                        borderRadius: "8px",
                        marginTop: "12px",
                        border: "2px solid #fcc",
                      }}
                    >
                      {error}
                    </div>
                  )}
                </div>
              </div>
            )}

            {stage === "success" && (
              <div
                className="success-container"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "32px",
                  alignItems: "center",
                  justifyContent: "center",
                  maxWidth: "1000px",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                {/* Doctor Character */}
                <div
                  className="doctor-character"
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    height: "260px",
                    width: "260px",
                  }}
                >
                  <img
                    src={doctorImage}
                    alt="Doctor"
                    style={{
                      height: "100%",
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                </div>

                {/* Success Message */}
                <div
                  className="content-box"
                  style={{
                    position: "relative",
                    flex: 1,
                    padding: "40px",
                    background: "white",
                    borderRadius: "20px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
                    border: "1px solid #e8e8e8",
                    textAlign: "center",
                    minWidth: "300px",
                  }}
                >
                  {/* Speech Bubble Pointer */}
                  <div
                    className="speech-pointer"
                    style={{
                      position: "absolute",
                      left: "-20px",
                      top: "60px",
                      width: "0",
                      height: "0",
                      borderTop: "15px solid transparent",
                      borderBottom: "15px solid transparent",
                      borderRight: "20px solid white",
                      filter: "drop-shadow(-2px 0px 2px rgba(0, 0, 0, 0.08))",
                    }}
                  />
                  {/* Mobile Speech Bubble Pointer */}
                  <div className="speech-pointer-mobile" />
                  <h3
                    style={{
                      fontSize: "32px",
                      marginBottom: "20px",
                      color: "#333",
                      fontWeight: "800",
                    }}
                  >
                    🎉 All Done!
                  </h3>
                  <p
                    style={{
                      fontSize: "16px",
                      marginBottom: "32px",
                      color: "#666",
                      lineHeight: "1.6",
                    }}
                  >
                    Patient <strong>{form.name}</strong> has been successfully
                    added to the system!
                  </p>
                  <button
                    onClick={() =>
                      navigate(
                        `/patients?newPatient=${newPatientId}&guide=true`,
                        { replace: true }
                      )
                    }
                    className="game-button"
                    style={{
                      padding: "14px 28px",
                      borderRadius: "8px",
                      border: "none",
                      background: "var(--primary)",
                      color: "white",
                      fontWeight: "700",
                      cursor: "pointer",
                      fontSize: "16px",
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-3px)";
                      e.target.style.boxShadow =
                        "0 4px 16px rgba(0, 0, 0, 0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                    }}
                  >
                    Go to Patients List ✓
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AddPatientPage;
